import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { ANNUALLY, MONTHLY, ONE_TIME_FEE } from 'pricing-pages-lib/constants/CalculatedPriceTerms';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { getHasProductInProductInventory, getProductsFromProductInventoryQuantity } from 'pricing-pages-lib/utils/productInventoryUtils';
import { getRequiredOnboardingSkuId } from 'pricing-pages-lib/utils/requiredOnboardingUtils';
import { calculatedPriceAdapterV3 } from 'self-service-api/adapters/calculatedPriceAdapter';
import { currentOrderedPaidProductLevels, STARTER } from 'self-service-api/constants/ProductLevels';
import { HUBSPOT_FOR_STARTUPS_SETUP } from 'self-service-api/constants/ApiNames';
import { QUANTITY_PACK } from 'self-service-api/constants/SkuTypes';
import { getCrmSuiteHighestTermsOfferId, getCrmSuiteLowestTermsOfferId, getCrmSuiteSeatsLowestTermsOfferId, getLowestTermsOfferId, getOfferIdForSkuInProductConfigurations, getOfferInfo } from './offerUtils';
import { getCrmSuiteProductLevelForProductConfigurations, getIsMarketingContactsSku, getProductForSkuId, getProductVersion, getSku, getSkuIdIsHub, getSkuIdIsInCrmSuite, isQuantifiableHub } from './productsUtils';
import { getIsEligibleForRetailPromotion } from './promotionUtils';
import { getIsUngatedForSeats } from './gatingUtils';

const calculatePrice = ({
  productData: {
    skuMap,
    productMap
  }
}, {
  purchaseConfig
}) => {
  const calculatedPrice = calculatedPriceAdapterV3({
    purchaseConfig,
    productMap,
    skuMap
  });
  return Object.assign({}, purchaseConfig.productConfigurations[0], {}, calculatedPrice);
};

const createPurchaseConfig = ({
  currencyCode,
  offerInfo,
  productConfigurations,
  offerId
}) => {
  const {
    discount: discountPercentage
  } = offerInfo || {};
  return {
    currencyCode,
    productConfigurations,
    offerId,
    discountPercentage
  };
}; // Used to transform the calculated price object when we want to position discounted
// price as the base price.


const transformDiscountedPriceAndOfferConfigToBase = (state, {
  offerId,
  lowestTermsOfferId,
  productConfiguration
}) => {
  const {
    currencyCode
  } = state;
  const currentTermsCalculatedPrice = calculatePrice(state, {
    purchaseConfig: createPurchaseConfig({
      currencyCode,
      productConfigurations: [productConfiguration],
      offerId,
      offerInfo: getOfferInfo(state, {
        offerId
      })
    })
  });
  const lowestTermsCalculatedPrice = calculatePrice(state, {
    purchaseConfig: createPurchaseConfig({
      currencyCode,
      productConfigurations: [productConfiguration],
      offerId: lowestTermsOfferId,
      offerInfo: getOfferInfo(state, {
        offerId: lowestTermsOfferId
      })
    })
  });
  const priceConfigKeys = [MONTHLY, ANNUALLY, ONE_TIME_FEE];
  const calculatedPrice = Object.assign({}, currentTermsCalculatedPrice, {}, priceConfigKeys.reduce((acc, curr) => {
    const fullPrice = lowestTermsCalculatedPrice[curr].calculatedPrice;
    return Object.assign({}, acc, {
      [curr]: Object.assign({}, currentTermsCalculatedPrice[curr], {
        fullPrice
      })
    });
  }, {}));
  const newDiscount = (1 - calculatedPrice['monthly'].calculatedPrice / calculatedPrice['monthly'].fullPrice) * 100;
  const offerInfo = Object.assign({}, getOfferInfo(state, {
    offerId
  }), {
    discount: newDiscount
  });
  return Object.assign({}, calculatedPrice, {
    offerInfo
  });
};

export const mergePriceObjects = (...priceObjects) => {
  const totalPrices = Object.assign({}, priceObjects[0], {
    monthly: {
      fullPrice: 0,
      calculatedPrice: 0,
      priceBreakdown: []
    },
    annually: {
      fullPrice: 0,
      calculatedPrice: 0,
      priceBreakdown: []
    },
    oneTimeFee: {
      fullPrice: 0,
      calculatedPrice: 0,
      priceBreakdown: []
    }
  });
  priceObjects.forEach(priceObject => {
    if (priceObject) {
      [MONTHLY, ANNUALLY, ONE_TIME_FEE].forEach(section => {
        const castedSection = section;
        totalPrices[castedSection].fullPrice = totalPrices[castedSection].fullPrice + priceObject[castedSection].fullPrice;
        totalPrices[castedSection].calculatedPrice = totalPrices[castedSection].calculatedPrice + priceObject[castedSection].calculatedPrice;
      });
    }
  });
  return totalPrices;
};
export const subtractPriceObjects = (...priceObjects) => {
  const totalPrices = priceObjects.length ? JSON.parse(JSON.stringify(priceObjects[0])) : {};
  priceObjects.slice(1).forEach(priceObject => {
    if (priceObject) {
      [MONTHLY, ANNUALLY, ONE_TIME_FEE].forEach(section => {
        const castedSection = section;
        totalPrices[castedSection].fullPrice = totalPrices[castedSection].fullPrice - priceObject[castedSection].fullPrice;
        totalPrices[castedSection].calculatedPrice = totalPrices[castedSection].calculatedPrice - priceObject[castedSection].calculatedPrice;
      });
    }
  });
  return totalPrices;
};

const getProductsFromProductInventoryQuantityForShowingAdditionalPrice = (state, {
  productConfiguration,
  allProductConfigurations
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const sku = skuMap[productConfiguration.skuId];

  if (sku.skuType === QUANTITY_PACK) {
    const {
      productVersionSkuId
    } = sku;

    if (!allProductConfigurations.some(({
      skuId
    }) => skuId === productVersionSkuId)) {
      const ownedQuantity = getProductsFromProductInventoryQuantity(state, {
        skuId: sku.skuId
      });
      return ownedQuantity;
    }
  }

  return productConfiguration.quantity;
};
/**
 * Returns the price breakdown for a specific product configuration in a list of product
 * configurations.
 */


export const getPriceBreakdownForProductConfigurationInList = (state, {
  allProductConfigurations,
  productConfiguration,
  queryParamTerm,
  adjustPricesForOwnership = false,
  useHighestSuiteOfferTerms = false
}) => {
  const {
    currencyCode
  } = state;
  const suiteProductLevel = getCrmSuiteProductLevelForProductConfigurations(state, {
    productConfigurations: allProductConfigurations
  });
  const isUngatedForSeats = getIsUngatedForSeats(state);
  const productVersion = getProductVersion(state, {
    skuId: productConfiguration.skuId
  });
  if (!productVersion) throw new Error(`productVersion was undefined for skuId ${productConfiguration.skuId}`);
  const {
    skuId
  } = productVersion;
  const {
    productLevel,
    productLine
  } = getProductForSkuId(state, {
    skuId
  });
  const offerIdToUse = getOfferIdForSkuInProductConfigurations(state, {
    allProductConfigurations,
    productConfiguration,
    queryParamTerm,
    useHighestSuiteOfferTerms
  });
  const offerInfo = offerIdToUse ? getOfferInfo(state, {
    offerId: typeof offerIdToUse === 'number' ? offerIdToUse : offerIdToUse[productLine]
  }) : null;

  if (!productConfiguration || !offerIdToUse) {
    return null;
  }

  const purchaseConfig = createPurchaseConfig({
    currencyCode,
    productConfigurations: [productConfiguration],
    offerInfo: getOfferInfo(state, {
      offerId: typeof offerIdToUse === 'number' ? offerIdToUse : offerIdToUse[productLine]
    }),
    offerId: offerIdToUse
  });
  let calculatedPrice = calculatePrice(state, {
    purchaseConfig
  });

  if (adjustPricesForOwnership && 'productInventory' in state) {
    const productsFromProductInventoryQuantity = getProductsFromProductInventoryQuantityForShowingAdditionalPrice(state, {
      productConfiguration,
      allProductConfigurations
    });

    if (productsFromProductInventoryQuantity !== productConfiguration.quantity) {
      const ownedPurchaseConfig = createPurchaseConfig({
        currencyCode,
        productConfigurations: [createProductConfig({
          skuId: productConfiguration.skuId,
          quantity: productsFromProductInventoryQuantity
        })],
        offerInfo: getOfferInfo(state, {
          offerId: typeof offerIdToUse === 'number' ? offerIdToUse : offerIdToUse[productLine]
        }),
        offerId: offerIdToUse
      });
      const ownedPrice = calculatePrice(state, {
        purchaseConfig: ownedPurchaseConfig
      });
      calculatedPrice = Object.assign({}, subtractPriceObjects(calculatedPrice, ownedPrice), {
        quantity: calculatedPrice.quantity - productsFromProductInventoryQuantity
      });
    }
  }

  const isEligibleForRetailPromotion = getIsEligibleForRetailPromotion(state, {
    skuId
  });
  const skuIdIsHub = getSkuIdIsHub(state, {
    skuId: productConfiguration.skuId
  });
  const skuIdIsQuantifiableHub = isUngatedForSeats && isQuantifiableHub(state, {
    skuId: productConfiguration.skuId
  });
  const isAlaCarteEligibleForRetailPromotion = isEligibleForRetailPromotion && productLevel === STARTER && !suiteProductLevel;
  const isCrmSuiteHub = suiteProductLevel && skuIdIsHub && getSkuIdIsInCrmSuite(state, {
    skuId,
    suiteProductLevel
  });

  if (isAlaCarteEligibleForRetailPromotion || isCrmSuiteHub) {
    const lowestTermsOfferId = suiteProductLevel ? skuIdIsQuantifiableHub ? getCrmSuiteSeatsLowestTermsOfferId(state, {
      productLevel: suiteProductLevel || productLevel
    }) : useHighestSuiteOfferTerms ? getCrmSuiteHighestTermsOfferId(state, {
      productLevel: suiteProductLevel || productLevel
    }) : getCrmSuiteLowestTermsOfferId(state, {
      productLevel: suiteProductLevel || productLevel
    }) : getLowestTermsOfferId(state, {
      skuId
    });
    return transformDiscountedPriceAndOfferConfigToBase(state, {
      offerId: typeof offerIdToUse === 'number' ? offerIdToUse : offerIdToUse[productLine],
      lowestTermsOfferId,
      productConfiguration
    });
  }

  return Object.assign({
    offerInfo
  }, calculatedPrice);
};
export const getPriceBreakdown = (state, {
  productConfigurations,
  queryParamTerm,
  adjustPricesForOwnership = false,
  useHighestSuiteOfferTerms = false
}) => {
  return productConfigurations.map(productConfiguration => {
    return getPriceBreakdownForProductConfigurationInList(state, {
      allProductConfigurations: productConfigurations,
      productConfiguration,
      queryParamTerm,
      adjustPricesForOwnership,
      useHighestSuiteOfferTerms
    });
  });
};
export const getCalculatedPrice = (state, args) => {
  const priceBreakdown = getPriceBreakdown(state, args);
  return mergePriceObjects(...priceBreakdown);
};
export const getPlanAdditionalCosts = (state, args) => {
  const {
    productConfigurations
  } = args;
  const calculatedPrice = getCalculatedPrice(state, args);
  const ownedProductConfigs = productConfigurations.filter(({
    skuId,
    quantity
  }) => {
    const {
      id: productId
    } = getProductForSkuId(state, {
      skuId
    });
    return getHasProductInProductInventory(state, {
      productAttributeFilters: {
        productId
      }
    }) && quantity > 0;
  }).map(({
    skuId
  }) => {
    const tempConfig = createProductConfig({
      skuId,
      quantity: getProductsFromProductInventoryQuantity(state, {
        skuId
      })
    });
    const adjustedQuantity = getProductsFromProductInventoryQuantityForShowingAdditionalPrice(state, {
      productConfiguration: tempConfig,
      allProductConfigurations: productConfigurations
    });
    return createProductConfig({
      skuId,
      quantity: adjustedQuantity
    });
  });
  const ownedCalculatedPrice = getCalculatedPrice(state, Object.assign({}, args, {
    productConfigurations: ownedProductConfigs
  }));
  return subtractPriceObjects(calculatedPrice, ownedCalculatedPrice);
};
/**
 * Returns the calculated price for a specific product configuration in a list of product
 * configurations.
 */

export const getCalculatedPriceForProductConfigurationsInList = (state, args) => {
  const {
    productConfigurations
  } = args,
        passThroughArgs = _objectWithoutPropertiesLoose(args, ["productConfigurations"]);

  const priceBreakdown = productConfigurations.map(productConfiguration => {
    return getPriceBreakdownForProductConfigurationInList(state, Object.assign({
      productConfiguration
    }, passThroughArgs));
  });
  return mergePriceObjects(...priceBreakdown);
};
export const getPlanAdditionalCostsForProductConfigurationsInList = (state, args) => {
  const {
    allProductConfigurations,
    productConfigurations
  } = args;
  const calculatedPrice = getCalculatedPriceForProductConfigurationsInList(state, args);
  const ownedProductConfigs = productConfigurations.filter(({
    skuId,
    quantity
  }) => {
    const {
      id: productId
    } = getProductForSkuId(state, {
      skuId
    });
    return getHasProductInProductInventory(state, {
      productAttributeFilters: {
        productId
      }
    }) && quantity > 0;
  }).map(({
    skuId
  }) => {
    const tempConfig = createProductConfig({
      skuId,
      quantity: getProductsFromProductInventoryQuantity(state, {
        skuId
      })
    });
    const adjustedQuantity = getProductsFromProductInventoryQuantityForShowingAdditionalPrice(state, {
      productConfiguration: tempConfig,
      allProductConfigurations
    });
    return createProductConfig({
      skuId,
      quantity: adjustedQuantity
    });
  });
  const ownedCalculatedPrice = getCalculatedPriceForProductConfigurationsInList(state, Object.assign({}, args, {
    productConfigurations: ownedProductConfigs
  }));
  return subtractPriceObjects(calculatedPrice, ownedCalculatedPrice);
};
/**
 * Groups one-time fees by productLevel. Useful for combining copy for multiple onboarding fees
 */

export const getGroupedFeesByProductLevel = (state, {
  skuIds,
  queryParamTerm
}) => {
  const USE_DIFFERENT_COPY = [HUBSPOT_FOR_STARTUPS_SETUP];
  const {
    productData: {
      skuMap
    }
  } = state;
  return skuIds.reduce((acc, skuId) => {
    const {
      skuType
    } = skuMap[skuId];
    const requiredOnboardingSkuId = getRequiredOnboardingSkuId(state, {
      skuId
    });

    if (requiredOnboardingSkuId && skuType !== QUANTITY_PACK) {
      const pricingInfo = getCalculatedPrice(state, {
        queryParamTerm,
        productConfigurations: [createProductConfig({
          skuId: requiredOnboardingSkuId
        })]
      });
      if (!pricingInfo) return acc;
      const {
        productLevel
      } = getProductForSkuId(state, {
        skuId
      });
      const {
        apiName: onboardingApiName
      } = getProductForSkuId(state, {
        skuId: requiredOnboardingSkuId
      });

      if (USE_DIFFERENT_COPY.includes(onboardingApiName)) {
        acc[onboardingApiName] = pricingInfo;
      } else if (currentOrderedPaidProductLevels.includes(productLevel)) {
        if (productLevel in acc) {
          acc[productLevel] = mergePriceObjects(acc[productLevel], pricingInfo);
        } else {
          acc[productLevel] = pricingInfo;
        }
      }
    }

    return acc;
  }, {});
};
export const getContactTierPriceMap = (state, {
  quantityPackSkuId,
  queryParamTerm,
  allProductConfigurations
}) => {
  const {
    currencyCode
  } = state;
  const quantityPack = getSku(state, {
    skuId: quantityPackSkuId
  });
  const {
    productLine
  } = getProductForSkuId(state, {
    skuId: quantityPackSkuId
  });
  const {
    quantityUnit: {
      includedWithProductVersion,
      includedWithQuantityPack
    },
    productVersionSkuId
  } = quantityPack;
  const isMarketingContactsSku = getIsMarketingContactsSku(state, {
    skuId: productVersionSkuId
  });
  const pricingInfo = quantityPack.pricingWrap.tieredPrices.find(({
    currencyCode: searchCurrencyCode
  }) => currencyCode === searchCurrencyCode);
  if (!pricingInfo) throw new Error(`Cannot find priceInfo for quantity pack skuId ${quantityPackSkuId}`);
  let priceTiers = pricingInfo.priceTiers;
  let offerId = null;

  if (queryParamTerm) {
    offerId = getOfferIdForSkuInProductConfigurations(state, {
      allProductConfigurations,
      productConfiguration: createProductConfig({
        skuId: quantityPackSkuId,
        quantity: 1
      }),
      queryParamTerm
    });
  }

  if (!offerId && !queryParamTerm) {
    offerId = getLowestTermsOfferId(state, {
      skuId: quantityPackSkuId
    });
  }

  if (offerId) {
    const {
      discount
    } = getOfferInfo(state, {
      offerId: typeof offerId === 'number' ? offerId : offerId[productLine]
    }) || {
      discount: 0
    };
    priceTiers = priceTiers.map(priceTier => Object.assign({}, priceTier, {
      value: priceTier.value * (1 - discount / 100)
    }));
  } // Need to create an artificial first tier in the prices
  // for included units on marketing contacts skus


  if (isMarketingContactsSku) {
    const includedTierUnits = includedWithProductVersion / includedWithQuantityPack;
    priceTiers = priceTiers.map((_ref) => {
      let {
        lowerBound,
        upperBound
      } = _ref,
          rest = _objectWithoutPropertiesLoose(_ref, ["lowerBound", "upperBound"]);

      return Object.assign({}, rest, {
        lowerBound: lowerBound + includedTierUnits,
        upperBound: upperBound == null ? null : upperBound + includedTierUnits
      });
    }); // Annoyingly, only the first lower bound in price tiers is inclusive
    // so gotta make it exclusive when bumped up

    if (priceTiers.length > 0) priceTiers[0].lowerBound++;
    priceTiers.unshift({
      lowerBound: 0,
      upperBound: includedTierUnits,
      value: 0
    });
  }

  return priceTiers;
};
export const getHighestPossiblePrice = (state, args) => {
  const calculatedPriceAnnual = getCalculatedPrice(state, Object.assign({
    queryParamTerm: 'annual'
  }, args));
  const calculatedMonthlyPrice = getCalculatedPrice(state, Object.assign({
    queryParamTerm: 'monthly'
  }, args));
  return calculatedPriceAnnual.monthly.calculatedPrice > calculatedMonthlyPrice.monthly.calculatedPrice ? calculatedPriceAnnual : calculatedMonthlyPrice;
};